import {Component, OnInit, ViewChild} from '@angular/core';
import {PlanModelService} from '../../common/service/plan-model.service';
import {LookupService} from '../../common/service/lookup.service';
import {Locality} from '../../common/models/locality.model';
import {NgEventBus} from 'ng-event-bus';
import {Lga} from '../../common/models/lga.model';
import {NgClass} from '@angular/common';
import {ControlContainer, FormsModule, NgForm} from '@angular/forms';
import {NlrTypeheadDirective} from '../../common/directive/nlr-typehead.directive';
import {debounceTime, distinctUntilChanged, map, Observable} from 'rxjs';
import {isDuplicateReference} from '../../common/service/data-util';
import {NlrHasPermissionsPipe} from '../../common/pipe/nlr-has-permissions.pipe';
import {PermissionsService} from '../../common/service/permissions.service';
import {NlrFocusDirective} from '../../common/directive/nlr-focus.directive';

@Component({
    selector: 'nlr-locality-and-lga',
    imports: [FormsModule, NlrTypeheadDirective, NgClass, NlrHasPermissionsPipe, NlrFocusDirective],
    templateUrl: './locality-and-lga.component.html',
    styleUrl: './locality-and-lga.component.scss',
    viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class LocalityAndLgaComponent implements OnInit {
    title = 'Locality and local government';

    localities: Locality[] | undefined;

    lgas: Lga[] | undefined;

    editViewInformationPermissions = ['EDIT_INFORMATION', 'VIEW_INFORMATION'];

    get readonly() {
        return this.planModelService.readonly;
    }
    @ViewChild('locality', {static: false}) myInputElement: HTMLInputElement;

    constructor(
        private eventBus: NgEventBus,
        private lookupService: LookupService,
        private permissionsService: PermissionsService,
        private planModelService: PlanModelService
    ) {}

    ngOnInit() {
        this.initialise();
    }

    private initialise() {
        this.getLocalities();
        this.getLGAs();
        if (this.getModel().localities.length === 0) {
            this.getModel().localities.push({localityName: '', id: undefined});
        }
        if (this.getModel().localGovernmentAuthorities.length === 0) {
            this.getModel().localGovernmentAuthorities.push({lgaName: '', id: undefined});
        }
    }

    filterLocalitiesByFullName: (text$: Observable<string>) => Observable<string[]> = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map(term => (term.length < 1 ? [] : this.filterLocalitiesByValue(term)))
        );

    filterLocalitiesByValue(viewValue) {
        return this.localities
            .filter(locality => locality.name.toLowerCase().startsWith(viewValue.toLowerCase()) && !this.checkForDuplicateLocalities(locality.name))
            .slice(0, 10)
            .map(l => l.name);
    }

    getModel() {
        return this.planModelService.getModel();
    }

    addLocality() {
        this.getModel().localities.push({localityName: '', id: undefined});
    }

    allowAdditionalLocalities() {
        const emptyRows = this.getModel().localities.filter(row => {
            return row.localityName === '' || row.localityName === undefined;
        });
        return emptyRows.length === 0;
    }

    removeLocality(index: number) {
        this.getModel().localities.splice(index, 1);
    }

    allowAdditionalLgas() {
        const emptyRows = this.getModel().localGovernmentAuthorities.filter(row => {
            return row.lgaName === '' || row.lgaName === undefined;
        });
        return emptyRows.length === 0;
    }

    filterLgasByFullName: (text$: Observable<string>) => Observable<string[]> = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map(term => (term.length < 1 ? [] : this.filterLgasByViewValue(term)))
        );

    filterLgasByViewValue(viewValue) {
        return this.lgas
            .filter(lga => {
                return (
                    lga.localGovernmentAuthority.toLowerCase().indexOf(viewValue.toLowerCase()) !== -1 &&
                    !this.checkForDuplicateLgas(lga.localGovernmentAuthority)
                );
            })
            .slice(0, 10)
            .map(l => l.localGovernmentAuthority);
    }

    addLga() {
        this.getModel().localGovernmentAuthorities.push({lgaName: '', id: undefined});
    }

    removeLga(index) {
        this.getModel().localGovernmentAuthorities.splice(index, 1);
    }

    localityChanged(element) {
        console.log(element);
    }

    localitySelected(item) {
        const locality = this.localities.find(loc => loc.name === item);
        console.log('item ', item);
        this.getModel().localities = this.getModel().localities.map(l => (l.localityName === item ? {...l, ...locality} : l));
        this.eventBus.cast('nlr:plan-model-updated:localities', 'localities');
    }

    isDuplicateLocality(i: number) {
        const localityToCheck = this.getModel().localities[i]?.localityName;
        if (localityToCheck) {
            const localityNames = this.getModel().localities.map(l => l.localityName);
            return isDuplicateReference(localityNames, localityToCheck);
        }
        return false;
    }

    isDuplicateLga(i: number) {
        const checkValue = this.getModel().localGovernmentAuthorities[i]?.lgaName;
        if (checkValue) {
            const lgaNames = this.getModel().localGovernmentAuthorities.map(l => l.lgaName);
            return isDuplicateReference(lgaNames, checkValue);
        }
        return false;
    }

    checkForDuplicateLocalities(localityName) {
        // return true if more than 1
        return this.getModel().localities.filter(locality => locality.localityName === localityName).length > 1;
    }

    checkForDuplicateLgas(lgaName) {
        return this.getModel().localGovernmentAuthorities.filter(row => row.lgaName === lgaName).length > 1;
    }

    isValidLga(i: number) {
        if (!this.lgas) return true;
        return this.lgas.some(l => l.localGovernmentAuthority === this.getModel().localGovernmentAuthorities[i]?.lgaName);
    }

    isValidLocality(i: number) {
        if (!this.localities) return true;
        return this.localities.some(l => l.name === this.getModel().localities[i]?.localityName);
    }

    lgaSelected(item) {
        const lga = this.lgas.find(l => l.localGovernmentAuthority === item);
        console.log('item ', item);
        this.getModel().localGovernmentAuthorities = this.getModel().localGovernmentAuthorities.map(l => (l.lgaName === item ? {...l, ...lga} : l));
        this.eventBus.cast('nlr:plan-model-updated:lga', 'lga');
    }

    getLocalities() {
        const subs = this.lookupService.retrieveLookup<Locality[]>('/locationname/findAllLocalities').subscribe({
            next: data => {
                this.localities = data;
                console.log('Localities load success ');
            },
            error: error => {
                console.error('Localities Load failed ' + error.message);
            }
        });
        this.eventBus.cast('nlr:busy', subs);
    }

    getLGAs() {
        const subs = this.lookupService.retrieveLookup<Lga[]>('/locationname/findAllLocalGovernmentAuthorities').subscribe({
            next: data => {
                this.lgas = data;
                console.log('LGAs load success ');
            },
            error: error => {
                console.error('LGAs Load failed ' + error.message);
            }
        });
        this.eventBus.cast('nlr:busy', subs);
    }

    isDisabled() {
        return this.planModelService.readonly || this.permissionsService.isDisabledByPermissions(this.editViewInformationPermissions);
    }
}
